/**
 * <s-film-reel> plays The Veyilon Story like a film.
 *
 * One wheel turn, arrow key, Page Up/Down or Space glides to the next or
 * previous scene in slow motion instead of scrolling freely. Each scene is
 * marked data-film-active while it is on screen, which starts its subtitles
 * and slow push-in (see "THE VEYILON STORY: A FILM" in config/styles.css).
 *
 * Touch screens keep their natural swipe, and visitors who prefer reduced
 * motion keep ordinary scrolling.
 */
const GLIDE_MS = 2400;
const SETTLE_MS = 650;

/** @param {number} t */
const easeInOutQuint = (t) => (t < 0.5 ? 16 * t ** 5 : 1 - (-2 * t + 2) ** 5 / 2);

class FilmReel extends HTMLElement {
  /** @type {HTMLElement[]} */
  scenes = [];
  gliding = false;
  settleUntil = 0;
  /** @type {IntersectionObserver | null} */
  observer = null;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.scenes = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-film-scene]')]);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const scene = /** @type {HTMLElement} */ (entry.target);
          scene.toggleAttribute('data-film-active', entry.isIntersecting);
        }
      },
      { threshold: 0.55 }
    );
    for (const scene of this.scenes) this.observer.observe(scene);

    window.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('keydown', this.handleKey);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('keydown', this.handleKey);
  }

  /** @param {number} direction 1 for forward, -1 for back */
  targetFor(direction) {
    if (this.reducedMotion.matches) return null;
    const y = window.scrollY;
    const viewport = window.innerHeight;
    const tops = this.scenes.map((scene) => scene.getBoundingClientRect().top + y);
    const first = tops[0];
    const last = tops[tops.length - 1];
    if (first === undefined || last === undefined) return null;

    if (direction > 0) {
      if (y + viewport <= first || y >= last - 4) return null;
      return tops.find((top) => top > y + 4) ?? null;
    }
    if (y <= first + 4 || y > last + 4) return null;
    return [...tops].reverse().find((top) => top < y - 4) ?? null;
  }

  /** @param {number} to */
  glide(to) {
    const root = document.documentElement;
    const from = window.scrollY;
    const start = performance.now();
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    this.gliding = true;

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / GLIDE_MS, 1);
      window.scrollTo(0, from + (to - from) * easeInOutQuint(progress));
      if (progress < 1) {
        requestAnimationFrame(step);
        return;
      }
      root.style.scrollBehavior = previousBehavior;
      this.gliding = false;
      this.settleUntil = performance.now() + SETTLE_MS;
    };
    requestAnimationFrame(step);
  }

  /** @param {number} direction */
  advance(direction) {
    const target = this.targetFor(direction);
    if (target === null) return false;
    this.glide(target);
    return true;
  }

  /** @param {WheelEvent} event */
  handleWheel = (event) => {
    if (event.ctrlKey || this.reducedMotion.matches) return;
    if (this.gliding || performance.now() < this.settleUntil) {
      if (this.targetFor(Math.sign(event.deltaY) || 1) !== null || this.gliding) event.preventDefault();
      return;
    }
    if (Math.abs(event.deltaY) < 4) return;
    if (this.advance(Math.sign(event.deltaY))) event.preventDefault();
  };

  /** @param {KeyboardEvent} event */
  handleKey = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = /** @type {Element | null} */ (event.target);
    if (target?.closest('input, textarea, select, [contenteditable], dialog')) return;

    let direction = 0;
    if (event.key === 'ArrowDown' || event.key === 'PageDown') direction = 1;
    if (event.key === 'ArrowUp' || event.key === 'PageUp') direction = -1;
    if (event.key === ' ' && !target?.closest('button, a, summary')) direction = event.shiftKey ? -1 : 1;
    if (direction === 0) return;

    if (this.gliding) {
      event.preventDefault();
      return;
    }
    if (this.advance(direction)) event.preventDefault();
  };
}

if (!customElements.get('s-film-reel')) customElements.define('s-film-reel', FilmReel);
